import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeBusinessRole } from '../../lib/role';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import PhoneInput from '../ui/PhoneInput';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Phone, BarChart3, Shield, Users, Headphones, Brain, Crown, Zap, Globe, Clock, TrendingUp, Lock, Cloud, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ProfileCompletion from './ProfileCompletion';
import TenantSelector from './TenantSelector';
import { verifySelectedTenant, clearTenantScopedState } from '../../lib/tenantSession';
import { auth as firebaseAuth } from '../../lib/firebase';
import { ServisfyLogo } from '../Common/ServisfyLogo';
import LanguageSelector from '../Common/LanguageSelector';

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    phoneNumber: '',
    otpCode: '',
    role: ''
  });
  const [currentStep, setCurrentStep] = useState('role'); // 'role' | 'phone' | 'otp' | 'superadmin' | 'profileCompletion' | 'tenantSelection'
  const [otpTimer, setOtpTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedBenefit, setExpandedBenefit] = useState(null);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  // PMT1: set only when the backend reports the verified person holds >1 active membership.
  const [pendingMemberships, setPendingMemberships] = useState(null);
  const [tenantSelectError, setTenantSelectError] = useState(null);
  
  const { login } = useAuth();
  const { registerPhoneNumber, verifyOTP, resendOTP } = useCRMAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  
  // OTP timer countdown
  React.useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Handle clicking outside benefit cards to collapse them
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedBenefit !== null && !event.target.closest('.benefit-card')) {
        setExpandedBenefit(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedBenefit]);

  // B6A-3 (web=Business plane only): Customer is NOT a Web CRM role. The Customer
  // plane is Android/iOS only and has its own login; web customer login is intentionally
  // not offered here. The /customer/* dashboard route + CRM/customer auth remain intact
  // (no global removal of customer capability).
  const roleDefinitions = {
    admin: {
      title: t('role.admin'),
      description: 'System configuration, user management, data quality control',
      icon: Shield,
      color: 'bg-purple-100 text-purple-800',
      features: ['User Management', 'System Configuration', 'Data Quality Control', 'Access Control']
    },
    operator: {
      title: t('role.operator'),
      description: 'Daily execution, contact management, customer communications',
      icon: Headphones,
      color: 'bg-blue-100 text-blue-800',
      features: ['Contact Management', 'Customer Communication', 'Issue Resolution', 'Pipeline Management']
    },
    manager: {
      title: t('role.manager'),
      description: 'Team performance monitoring, reporting, campaign tracking',
      icon: Users,
      color: 'bg-orange-100 text-orange-800',
      features: ['Team Performance', 'Reporting & Forecasting', 'Campaign Tracking', 'Process Optimization']
    },
    analyst: {
      title: t('role.analyst'),
      description: 'Advanced reporting, data mining, specialized insights',
      icon: BarChart3,
      color: 'bg-teal-100 text-teal-800',
      features: ['Advanced Analytics', 'Data Mining', 'Trend Analysis', 'Performance Metrics']
    },
    director: {
      title: t('role.director'),
      description: 'Strategic alignment, AI insights, customer strategy',
      icon: BarChart3,
      color: 'bg-indigo-100 text-indigo-800',
      features: ['Strategic Analytics', 'AI Insights', 'ROI Optimization', 'Customer Strategy']
    },
    executive: {
      title: t('role.executive'),
      description: 'Business growth, executive dashboards, predictive analytics',
      icon: BarChart3,
      color: 'bg-rose-100 text-rose-800',
      features: ['Executive Dashboards', 'Predictive Analytics', 'Business Growth', 'High-level Decisions']
    },
    it: {
      title: t('role.it'),
      description: 'Technical infrastructure, API management, security',
      icon: Shield,
      color: 'bg-gray-100 text-gray-800',
      features: ['API Management', 'Security & Database', 'System Integration', 'Technical Infrastructure']
    }
  };

  const benefitCards = [
    {
      id: 1,
      title: t('benefit.1.title'),
      headline: t('benefit.1.headline'),
      icon: Zap,
      bullets: [t('benefit.1.b1'), t('benefit.1.b2')]
    },
    {
      id: 2,
      title: t('benefit.2.title'),
      headline: t('benefit.2.headline'),
      icon: Globe,
      bullets: [t('benefit.2.b1'), t('benefit.2.b2'), t('benefit.2.b3')]
    },
    {
      id: 3,
      title: t('benefit.3.title'),
      headline: t('benefit.3.headline'),
      icon: TrendingUp,
      bullets: [t('benefit.3.b1'), t('benefit.3.b2')]
    },
    {
      id: 4,
      title: t('benefit.4.title'),
      headline: t('benefit.4.headline'),
      icon: Headphones,
      bullets: [t('benefit.4.b1'), t('benefit.4.b2')]
    },
    {
      id: 5,
      title: t('benefit.5.title'),
      headline: t('benefit.5.headline'),
      icon: Brain,
      bullets: [t('benefit.5.b1'), t('benefit.5.b2')]
    },
    {
      id: 6,
      title: t('benefit.6.title'),
      headline: t('benefit.6.headline'),
      icon: BarChart3,
      bullets: [t('benefit.6.b1'), t('benefit.6.b2')]
    },
    {
      id: 7,
      title: t('benefit.7.title'),
      headline: t('benefit.7.headline'),
      icon: Lock,
      bullets: [t('benefit.7.b1'), t('benefit.7.b2')]
    },
    {
      id: 8,
      title: t('benefit.8.title'),
      headline: t('benefit.8.headline'),
      icon: Cloud,
      bullets: [t('benefit.8.b1'), t('benefit.8.b2')]
    }
  ];

  const handleBenefitClick = (benefitId) => {
    if (expandedBenefit === benefitId) {
      setExpandedBenefit(null);
    } else {
      setExpandedBenefit(benefitId);
    }
  };

  const handleRoleSelection = (role) => {
    setCredentials(prev => ({ ...prev, role }));
    setCurrentStep('phone');
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.phoneNumber || !credentials.role) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.enterPhone'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await registerPhoneNumber(credentials.phoneNumber);
      
      if (result.success) {
        toast({
          title: t('auth.toast.otpSentTitle'),
          description: t('auth.toast.otpSentDesc'),
        });
        setCurrentStep('otp');
        setOtpTimer(300); // 5 minutes
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔐 DEV TEST OTP: Use code "123456" to continue');
        }
      } else {
        toast({
          title: t('auth.toast.errorTitle'),
          description: result.error || t('auth.toast.sendFailed'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.unexpected'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    if (!credentials.otpCode || credentials.otpCode.length < 4) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.enterValidOtp'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await verifyOTP(credentials.phoneNumber, credentials.otpCode);
      
      if (result.success) {
        // Check if profile completion is needed
        if (result.needsProfileCompletion) {
          setNeedsProfileCompletion(true);
          setCurrentStep('profileCompletion');
          setIsSubmitting(false);
          return;
        }

        // Resolve tenant + CANONICAL role from the backend staff record. The role chosen on the login
        // form is only a hint — the authoritative role lives in db.staff_members. We ALWAYS resolve it
        // (not just when brand_id is missing) so client-side role gates — e.g. Solution Bank Approve/Reject,
        // escalation management — reflect the real staff role instead of the self-asserted selection.
        // (The backend independently re-authorizes every request from the staff record, so this only
        // corrects what the UI shows/enables; it can never grant real privilege.)
        let brandId = result.user.brand_id || null;
        let resolvedRole = credentials.role;
        let resolvedName = result.user.name || result.user.display_name || null;
        if (credentials.role !== 'customer') {
          try {
            // F-03: the lookup now discloses the tenant + canonical role only to a VERIFIED caller.
            // Firebase sign-in has already completed at this point, so present that identity; an
            // unauthenticated caller receives existence only, which is what closed the PII leak.
            let _idTok = null;
            try { _idTok = firebaseAuth.currentUser ? await firebaseAuth.currentUser.getIdToken() : null; } catch (_e) { _idTok = null; }
            const lookup = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/by-phone/${encodeURIComponent(credentials.phoneNumber)}`,
              _idTok ? { headers: { Authorization: `Bearer ${_idTok}` } } : undefined
            );
            if (lookup.ok) {
              const data = await lookup.json();
              // PMT1: this verified person holds memberships in MORE THAN ONE tenant. The backend
              // deliberately withholds a tenant + role here (fail-closed), so we must not enter any
              // business. Ask which one, then have the SERVER verify that choice.
              if (data.linked && data.requires_tenant_selection) {
                setPendingMemberships(Array.isArray(data.memberships) ? data.memberships : []);
                setTenantSelectError(null);
                setCurrentStep('tenantSelection');
                setIsSubmitting(false);
                return;
              }
              if (data.linked) {
                // Accept brand_id, falling back to business_id (staff docs vary). Keep an existing brandId.
                brandId = brandId || data.brand_id || data.business_id || null;
                // AUTHORITATIVE backend role overrides the login-form selection.
                if (data.role) resolvedRole = data.role;
                // Prefer the real staff name for the header over a phone fallback.
                if (!resolvedName) resolvedName = data.name || data.display_name || null;
              }
            }
          } catch (_e) { /* offline → fall back to the form-selected role (backend still authoritative) */ }
        }

        // Merge with crm_user_data already in storage (preserved by CRMAuthContext merge).
        // brandId from by-phone rescue takes precedence; fall back to what signup wrote.
        // This ensures brandId survives even when by-phone returns linked:false (e.g.
        // phone format mismatch or transient DB miss) as long as signup set it first.
        const existingCrmData = JSON.parse(localStorage.getItem('crm_user_data') || '{}');
        const userData = {
          id: result.user.id,
          email: result.user.phone_number, // Use phone as email for compatibility
          phone: result.user.phone_number,
          role: normalizeBusinessRole(resolvedRole),  // canonical role so case-sensitive guards accept it
          name: resolvedName || credentials.phoneNumber,
          brandId: brandId || existingCrmData.brandId || null,
          loginTime: new Date().toISOString(),
          authMethod: 'phone_otp'
        };

        // CUSTOMER WEB = NONE. A customer identity must not enter Business Web. Servisfy's
        // supported customer planes are the Android and iOS Customer apps; there is deliberately
        // NO web destination for customers, so this is a refusal, not a redirect. Checked BEFORE
        // the session is established so no customer session is ever created on this plane.
        if (normalizeBusinessRole(credentials.role) === 'customer') {
          toast({
            title: t('auth.toast.errorTitle'),
            description: t('auth.customerWebUnsupported'),
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        // Persist full enriched user data (role, brandId) so AuthContext reads
        // the correct role/tenant on page refresh — not just uid+phoneNumber.
        localStorage.setItem('crm_user_data', JSON.stringify(userData));
        await login(userData);

        toast({
          title: t('auth.toast.loginSuccessTitle'),
          description: t('auth.toast.loginSuccessDesc', { role: (roleDefinitions[credentials.role] || roleDefinitions[normalizeBusinessRole(credentials.role)] || { title: 'User' }).title }),
        });

        navigate('/business');
      } else {
        toast({
          title: t('auth.toast.errorTitle'),
          description: result.error || t('auth.toast.invalidOtp'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.verifyFailed'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * PMT1 tenant selection. The tapped business is only a REQUESTED context: the backend
   * re-verifies the membership (active row in THAT tenant, tenant not suspended) and returns the
   * role FOR THAT TENANT. A fabricated / foreign / revoked tenant is denied here and no session is
   * written. Nothing tenant-scoped from a previous context survives the switch.
   */
  const handleTenantSelected = async (brandId) => {
    setTenantSelectError(null);
    const getToken = async () => {
      try { if (firebaseAuth.currentUser) return await firebaseAuth.currentUser.getIdToken(); } catch (_e) { /* fall through */ }
      return localStorage.getItem('crm_access_token') || '';
    };
    try {
      const verified = await verifySelectedTenant(getToken, brandId);
      clearTenantScopedState();
      const existingCrmData = JSON.parse(localStorage.getItem('crm_user_data') || '{}');
      const userData = {
        ...existingCrmData,
        email: credentials.phoneNumber,
        phone: credentials.phoneNumber,
        role: normalizeBusinessRole(verified.role),   // role of THIS tenant's membership only
        // PMT1 display identity. The backend returns the caller's OWN canonical staff name FOR THE
        // SELECTED TENANT on the already-membership-verified response, so it is the primary label.
        // A raw phone is the LAST resort — never preferred over a real name. (Defect fixed: this
        // seam previously had no name available and fell straight through to the phone number,
        // which then surfaced as the dashboard greeting and the sidebar identity.)
        name: verified.name || existingCrmData.name || credentials.phoneNumber,
        brandId: verified.brand_id,                    // server-verified, never the raw tap
        loginTime: new Date().toISOString(),
        authMethod: 'phone_otp',
      };
      localStorage.setItem('crm_user_data', JSON.stringify(userData));
      await login(userData);
      toast({
        title: t('auth.toast.loginSuccessTitle'),
        description: verified.business_name || verified.brand_id,
      });
      navigate('/business');
    } catch (err) {
      setTenantSelectError(
        err && err.message === 'membership_denied'
          ? t('tenantSelect.denied')
          : t('tenantSelect.failed')
      );
    }
  };

  const handleProfileComplete = async (updatedUser) => {
    try {
      const userData = {
        id: updatedUser.id,
        email: updatedUser.phone_number,
        phone: updatedUser.phone_number,
        role: credentials.role,
        name: updatedUser.name,
        brandId: updatedUser.brand_id || null,
        loginTime: new Date().toISOString(),
        authMethod: 'phone_otp'
      };

      // CUSTOMER WEB = NONE. A customer identity must not enter Business Web. Servisfy's
      // supported customer planes are the Android and iOS Customer apps; there is deliberately
      // NO web destination for customers, so this is a refusal, not a redirect. Checked BEFORE
      // the session is established so no customer session is ever created on this plane.
      if (normalizeBusinessRole(credentials.role) === 'customer') {
        toast({
          title: t('auth.toast.errorTitle'),
          description: t('auth.customerWebUnsupported'),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      localStorage.setItem('crm_user_data', JSON.stringify(userData));
      await login(userData);

      navigate('/business');
    } catch (error) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.completeFailed'),
        variant: "destructive",
      });
    }
  };

  const handleResendOTP = async () => {
    if (otpTimer > 0) return;

    try {
      const result = await resendOTP(credentials.phoneNumber);
      
      if (result.success) {
        toast({
          title: t('auth.toast.otpResentTitle'),
          description: t('auth.toast.otpResentDesc'),
        });
        setOtpTimer(300);
      } else {
        toast({
          title: t('auth.toast.errorTitle'),
          description: result.error || t('auth.toast.resendFailed'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t('auth.toast.errorTitle'),
        description: t('auth.toast.resendFailed'),
        variant: "destructive",
      });
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits && !value.startsWith('+')) {
      return '+' + digits;
    }
    return value;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show profile completion screen if needed
  if (currentStep === 'profileCompletion') {
    return <ProfileCompletion onComplete={handleProfileComplete} />;
  }

  // PMT1: the verified person belongs to more than one business — ask which one before entering any
  // of them. Rendered as its own full screen so no partially-scoped Business UI is reachable while
  // the tenant context is still undecided.
  if (currentStep === 'tenantSelection') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
        <div className="w-full max-w-md space-y-4">
          <div className="text-center">
            <ServisfyLogo variant="full" size="xl" className="mx-auto mix-blend-multiply" />
          </div>
          <TenantSelector
            memberships={pendingMemberships || []}
            onSelect={handleTenantSelected}
            error={tenantSelectError}
            cancelLabel={t('common.cancel')}
            onCancel={() => {
              setPendingMemberships(null);
              setTenantSelectError(null);
              setCurrentStep('role');
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      <div className="w-full max-w-6xl">
        {/* Public header: Pricing link + the EXISTING language selector. The LanguageSelector is
            preserved exactly (same component, className and LanguageContext) — the Pricing link is
            added ALONGSIDE it and must not obstruct or replace it. */}
        <div className="flex justify-end items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/pricing')}
            className="text-sm font-medium text-purple-700 hover:text-purple-900 hover:underline whitespace-nowrap"
          >
            Pricing
          </button>
          <LanguageSelector className="w-56 max-w-full" />
        </div>
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-3" data-testid="login-brand">
            <ServisfyLogo variant="full" size="xxl" className="mix-blend-multiply" />
          </div>
          <p className="text-xl text-gray-600">{t('auth.tagline')}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Login Form */}
          <Card className="backdrop-blur-sm bg-white/90 border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-center">{t('auth.signIn')}</CardTitle>
              <CardDescription className="text-center">
                {t('auth.signInDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentStep === 'role' && (
                <div className="space-y-6">
                  <div>
                    <Select 
                      value={credentials.role} 
                      onValueChange={handleRoleSelection}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder={t('auth.selectRole')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(roleDefinitions).map(([key, role]) => {
                          const IconComponent = role.icon;
                          return (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center">
                                <IconComponent className="h-4 w-4 mr-2" />
                                {role.title}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('auth.enhancedAuth')}</p>
                  </div>
                </div>
              )}

              {currentStep === 'phone' && (
                <form onSubmit={handlePhoneSubmit} className="space-y-6">
                  <div className="text-center mb-4">
                    <Badge className={roleDefinitions[credentials.role]?.color}>
                      {roleDefinitions[credentials.role]?.title}
                    </Badge>
                  </div>
                  
                  <div>
                    {/* Country-code picker + local number (parity with mobile). Emits E.164; no need to type +1. */}
                    <PhoneInput
                      value={credentials.phoneNumber}
                      onChange={(e164) => setCredentials(prev => ({ ...prev, phoneNumber: e164 }))}
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-gray-500 text-center mt-1">
                      {t('auth.phoneHelper')}
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700"
                    disabled={isSubmitting || !credentials.phoneNumber}
                  >
                    {isSubmitting ? t('auth.sendingOtp') : t('auth.sendOtp')}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep('role')}
                    className="w-full"
                  >
                    {t('auth.backToRole')}
                  </Button>
                </form>
              )}

              {currentStep === 'otp' && (
                <form onSubmit={handleOTPVerification} className="space-y-6">
                  <div className="text-center mb-4">
                    <Badge className={roleDefinitions[credentials.role]?.color}>
                      {roleDefinitions[credentials.role]?.title}
                    </Badge>
                    <p className="text-sm text-gray-600 mt-2">
                      {t('auth.sentTo', { phone: credentials.phoneNumber })}
                    </p>
                  </div>
                  
                  <div>
                    <Input
                      type="text"
                      placeholder={t('auth.otpPlaceholder')}
                      value={credentials.otpCode}
                      onChange={(e) => setCredentials(prev => ({ ...prev, otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      className="h-12 text-center text-2xl tracking-widest"
                      maxLength={6}
                      disabled={isSubmitting}
                    />
                    <div className="text-center mt-2">
                      {otpTimer > 0 && (
                        <p className="text-xs text-gray-500">
                          {t('auth.codeExpires', { time: formatTime(otpTimer) })}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-purple-600 hover:from-green-600 hover:to-purple-700"
                    disabled={isSubmitting || !credentials.otpCode || credentials.otpCode.length < 4}
                  >
                    {isSubmitting ? t('auth.verifying') : t('auth.verifyContinue')}
                  </Button>
                  
                  <div className="flex space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setCurrentStep('phone')}
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      {t('auth.changeNumber')}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleResendOTP}
                      disabled={isSubmitting || otpTimer > 0}
                      className="flex-1"
                    >
                      {otpTimer > 0 ? t('auth.resendTimer', { time: formatTime(otpTimer) }) : t('auth.resendOtp')}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Interactive Benefits Section */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">{t('auth.whyChoose')}</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {benefitCards.map((benefit) => {
                const IconComponent = benefit.icon;
                const isExpanded = expandedBenefit === benefit.id;
                
                return (
                  <Card 
                    key={benefit.id} 
                    className={`benefit-card cursor-pointer transition-all duration-300 ease-in-out hover:shadow-lg ${
                      isExpanded ? 'ring-2 ring-purple-500 bg-purple-50' : 'bg-white/70 hover:bg-white/90'
                    }`}
                    onClick={() => handleBenefitClick(benefit.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <IconComponent className={`h-6 w-6 mt-1 transition-colors duration-200 ${
                            isExpanded ? 'text-purple-600' : 'text-gray-600'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold text-sm leading-tight transition-colors duration-200 ${
                              isExpanded ? 'text-purple-900' : 'text-gray-900'
                            }`}>
                              {benefit.title}
                            </h4>
                            
                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isExpanded ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
                            }`}>
                              <p className="text-sm font-medium text-purple-800 mb-2">
                                {benefit.headline}
                              </p>
                              <ul className="space-y-1">
                                {benefit.bullets.map((bullet, index) => (
                                  <li key={index} className="text-xs text-gray-700 flex items-start">
                                    <span className="text-purple-500 mr-2 mt-1">•</span>
                                    <span>{bullet}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        
                        <div className={`ml-2 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}>
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sign Up Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            {t('auth.noAccount')}{' '}
            <button
              onClick={() => navigate('/signup')}
              className="text-purple-600 hover:text-purple-800 font-medium hover:underline"
            >
              {t('auth.signUpHere')}
            </button>
          </p>
          <p className="mt-2">
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm font-medium text-gray-600 hover:text-purple-700 hover:underline"
            >
              View Plans &amp; Pricing →
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;